import React, { useState } from 'react';
import { Link, Outlet, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const AdminLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const location = useLocation();
  const { user, logout, loading } = useAuth();

  // Si le chargement est en cours, afficher un loader
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cream">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-gray-500">Chargement...</p>
        </div>
      </div>
    );
  }

  // Vérifier si l'utilisateur est admin
  if (!user || user.role !== 'admin') {
    return <Navigate to="/login" replace />;
  }

  // Icônes SVG style line art
  const Icons = {
    Dashboard: () => (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
      </svg>
    ),
    Orders: () => (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
      </svg>
    ),
    Products: () => (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
      </svg>
    ),
    Users: () => (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
      </svg>
    ),
    Delivery: () => (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 13l4 4L19 7" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 17h2M19 17h2M12 21v-4M8 3h8M12 3v2" />
        <circle cx="8" cy="17" r="2" stroke="currentColor" strokeWidth={1.5} fill="none" />
        <circle cx="16" cy="17" r="2" stroke="currentColor" strokeWidth={1.5} fill="none" />
      </svg>
    ),
    Payments: () => (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 10a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
    ),
    Notifications: () => (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
      </svg>
    ),
    Reports: () => (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
    Logout: () => (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
      </svg>
    ),
    Menu: () => (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
      </svg>
    ),
    Close: () => (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
      </svg>
    )
  };

  const menuItems = [
    { path: '/admin', label: 'Dashboard', icon: <Icons.Dashboard /> },
    { path: '/admin/orders', label: 'Commandes', icon: <Icons.Orders /> },
    { path: '/admin/products', label: 'Produits', icon: <Icons.Products /> },
    { path: '/admin/users', label: 'Utilisateurs', icon: <Icons.Users /> },
    { path: '/admin/delivery', label: 'Livraisons', icon: <Icons.Delivery /> },
    { path: '/admin/payments', label: 'Paiements', icon: <Icons.Payments /> },
    { path: '/admin/notifications', label: 'Notifications', icon: <Icons.Notifications /> },
    { path: '/admin/reports', label: 'Rapports', icon: <Icons.Reports /> }
  ];

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Sidebar - Barre latérale admin uniquement */}
      <aside className={`fixed top-0 left-0 h-full bg-gradient-to-b from-primary to-primary/95 text-white transition-all duration-300 z-50 shadow-2xl ${sidebarOpen ? 'w-72' : 'w-20'}`}>
        <div className={`p-6 flex items-center ${sidebarOpen ? 'justify-between' : 'justify-center'} border-b border-white/10`}>
          {sidebarOpen ? (
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-secondary rounded-xl flex items-center justify-center shadow-lg">
                <span className="text-primary text-xl font-bold">A</span>
              </div>
              <div>
                <span className="font-playfair text-xl font-bold">AquaHome<span className="text-secondary">Delivery</span></span>
                <p className="text-xs text-gray-400">Administration</p>
              </div>
            </div>
          ) : (
            <div className="w-10 h-10 bg-secondary rounded-xl flex items-center justify-center mx-auto shadow-lg">
              <span className="text-primary text-lg font-bold">A</span>
            </div>
          )}
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="text-white/70 hover:text-white transition">
            {sidebarOpen ? <Icons.Close /> : <Icons.Menu />}
          </button>
        </div>

        <nav className="mt-8 px-3">
          {menuItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-4 px-4 py-3 rounded-xl transition-all duration-200 mb-1 group ${
                location.pathname === item.path
                  ? 'bg-secondary text-primary shadow-lg'
                  : 'text-white/80 hover:bg-white/10 hover:text-white'
              }`}
            >
              <div className={`transition-transform group-hover:scale-110 ${location.pathname === item.path ? 'text-primary' : ''}`}>
                {item.icon}
              </div>
              {sidebarOpen && <span className="font-medium">{item.label}</span>}
              {!sidebarOpen && (
                <div className="absolute left-full ml-2 px-2 py-1 bg-primary rounded-lg text-xs whitespace-nowrap opacity-0 group-hover:opacity-100 transition pointer-events-none">
                  {item.label}
                </div>
              )}
            </Link>
          ))}
          
          <div className="pt-6 mt-6 border-t border-white/10">
            <button
              onClick={logout}
              className="flex items-center gap-4 px-4 py-3 rounded-xl transition-all duration-200 w-full text-white/80 hover:bg-red-500/20 hover:text-red-400 group"
            >
              <Icons.Logout />
              {sidebarOpen && <span className="font-medium">Déconnexion</span>}
            </button>
          </div>
        </nav>

        {sidebarOpen && (
          <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/10">
            <p className="text-xs text-gray-400 text-center">© 2024 AquaHomeDelivery</p>
          </div>
        )}
      </aside>

      {/* Main content - Pas de Navbar/Footer client */}
      <main className={`transition-all duration-300 ${sidebarOpen ? 'ml-72' : 'ml-20'}`}>
        <div className="bg-white shadow-sm sticky top-0 z-40">
          <div className="px-6 py-4 flex justify-between items-center">
            <div>
              <h1 className="text-xl font-semibold text-darkGray">
                {menuItems.find(item => item.path === location.pathname)?.label || 'Dashboard'}
              </h1>
              <p className="text-sm text-gray-500 mt-0.5">
                Bienvenue, {user.firstName} {user.lastName}
              </p>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 bg-pearl px-3 py-1.5 rounded-full">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-xs text-gray-600">Mode administration</span>
              </div>
              <div className="w-8 h-8 bg-gradient-to-r from-primary to-secondary rounded-full flex items-center justify-center text-white font-bold">
                {user.firstName?.charAt(0)}{user.lastName?.charAt(0)}
              </div>
            </div>
          </div>
        </div>
        <div className="p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;